import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RotateCcw, Target, ArrowLeft, Search } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import { cn } from '@/lib/utils';
import heropaars from '@/assets/heropaars.png';
import heropaarsWebp from '@/assets/heropaars.webp';
import { SEO } from '@/components/SEO';
import { SITE_NAME, SITE_ORIGIN } from '@/seo/siteConfig';

// SpotlightCard Component
const SpotlightCard = ({ children, className = "", ...props }: any) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [gradientSize, setGradientSize] = useState(600);

  React.useEffect(() => {
    const updateGradientSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setGradientSize(300);
      } else if (width < 1024) {
        setGradientSize(450);
      } else {
        setGradientSize(600);
      }
    };

    updateGradientSize();
    window.addEventListener('resize', updateGradientSize);
    return () => window.removeEventListener('resize', updateGradientSize);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => { setOpacity(1); };
  const handleBlur = () => { setOpacity(0); };
  const handleMouseEnter = () => { setOpacity(1); };
  const handleMouseLeave = () => { setOpacity(0); };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl border border-transparent hover:border-[#854C73]/20 h-full",
        className
      )}
      {...props}
    >
      <div
        className="pointer-events-none absolute -inset-px transition duration-300 z-0"
        style={{
          opacity,
          background: `radial-gradient(${gradientSize}px circle at ${position.x}px ${position.y}px, rgba(133, 76, 115, 0.15), transparent 40%)`,
        }}
      />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>
  );
};

const FamilieOpstelling = () => {
  return (
    <>
      <SEO 
        title="Familieopstellingen Castricum, Amsterdam & Haarlem"
        description="Familieopstellingen in Castricum, Amsterdam, Haarlem en Uitgeest. Doorbreek patronen en krijg zicht op de onderstroom in jouw familiesysteem. Systemisch werk in heel Noord-Holland. Deelname via strippenkaart. Veilige setting, kleine groepen."
        keywords="familieopstelling Castricum, familieopstelling Amsterdam, familieopstelling Haarlem, systemisch werk Castricum, systemisch werk Amsterdam, familieopstelling Noord-Holland, familieopstelling Zaanstreek, familieopstelling Zaandam, familieopstelling Alkmaar, familieopstelling Hoofddorp, familieopstelling Uitgeest, systemisch werk Beverwijk, familieopstelling Heerhugowaard, opstellingenmiddagen Noord-Holland, familieopstelling strippenkaart"
        canonical="https://organisatieopstellingen.com/familieopstellingen"
        ogDescription="Familieopstellingen in Castricum en Amsterdam. Doorbreek patronen en krijg zicht op de onderstroom in jouw familiesysteem. Deelname via strippenkaart of los ticket."
        noindex
      />
      
      {/* Structured Data - Service (voor local SEO met areaServed) */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Familieopstellingen Service - Castricum en Amsterdam",
        "description": "Familieopstellingen in Castricum (hoofdlocatie) en Amsterdam. Doorbreek patronen en krijg zicht op de onderstroom in jouw familiesysteem. Vanuit Castricum goed bereikbaar voor Amsterdam, Haarlem en de regio Noord-Holland.",
        "provider": {
          "@type": "Organization",
          "name": SITE_NAME,
          "url": SITE_ORIGIN,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Castricum",
            "addressRegion": "Noord-Holland",
            "addressCountry": "NL"
          }
        },
        "areaServed": [
          {"@type": "City", "name": "Castricum"},
          {"@type": "City", "name": "Amsterdam"},
          {"@type": "City", "name": "Haarlem"},
          {"@type": "City", "name": "Zaandam"},
          {"@type": "City", "name": "Hoofddorp"},
          {"@type": "City", "name": "Amstelveen"},
          {"@type": "City", "name": "Hilversum"},
          {"@type": "City", "name": "Alkmaar"},
          {"@type": "City", "name": "Beverwijk"},
          {"@type": "City", "name": "Heemskerk"},
          {"@type": "City", "name": "Uitgeest"},
          {"@type": "City", "name": "Heerhugowaard"},
          {"@type": "City", "name": "Hoorn"},
          {"@type": "City", "name": "Purmerend"},
          {"@type": "AdministrativeArea", "name": "Zaanstreek"},
          {"@type": "State", "name": "Noord-Holland"}
        ],
        "serviceType": "Familieopstelling",
        "offers": {
          "@type": "Offer",
          "description": "Familieopstellingen in Castricum en Amsterdam"
        }
      })}
      </script>
      
      {/* Structured Data - BreadcrumbList */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${SITE_ORIGIN}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Familieopstellingen Castricum en Amsterdam",
            "item": `${SITE_ORIGIN}/familieopstellingen`
          }
        ]
      })}
      </script>
      
      {/* FAQPage Schema */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Wat is een familieopstelling?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Een familieopstelling is een systemische methode waarbij familiepatronen en dynamieken zichtbaar worden gemaakt. Door representanten of symbolen te gebruiken, krijg je inzicht in de onderstroom van jouw familiesysteem en hoe dit jouw keuzes, relaties en gevoel beïnvloedt. Familieopstellingen voor particulieren vind je op systemique.nl."
            }
          },
          {
            "@type": "Question",
            "name": "Waar vinden familieopstellingen plaats?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Familieopstellingen vinden plaats in Castricum (hoofdlocatie) en Amsterdam. Vanuit Castricum goed bereikbaar voor Amsterdam, Haarlem, Zaandam, Hoofddorp, Amstelveen, Hilversum en de regio Noord-Holland. Opstellingenmiddagen worden georganiseerd in Het Atelier, Uitgeest."
            }
          },
          {
            "@type": "Question",
            "name": "Hoe werkt een familieopstelling?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Tijdens een familieopstelling werk je met representanten of symbolen om de dynamieken in jouw familiesysteem zichtbaar te maken. Dit gebeurt in een veilige en respectvolle setting met kleine groepen. Je krijgt in één middag diepgaand inzicht in patronen en verbindingen. Familieopstellingen kunnen onderdeel zijn van een 1-op-1 coachtraject of je kunt deelnemen aan opstellingenmiddagen."
            }
          },
          {
            "@type": "Question",
            "name": "Kan ik een strippenkaart gebruiken voor familieopstellingen?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja, je kunt een strippenkaart gebruiken voor familieopstellingen. Met een strippenkaart kun je deelnemen aan opstellingenmiddagen als representant. Bekijk de agenda en koop een strippenkaart via systemique.nl/agenda."
            }
          },
          {
            "@type": "Question",
            "name": "Wat zijn opstellingenmiddagen?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Opstellingenmiddagen zijn georganiseerde middagen waar familie- en organisatieopstellingen plaatsvinden in Het Atelier, Uitgeest. Je kunt deelnemen als vraagsteller (met je eigen vraag) of als representant (om te leren en ervaren). Opstellingenmiddagen vinden plaats van 13:00 tot 16:30. Je kunt deelnemen met een strippenkaart of een los ticket kopen. Bekijk de agenda voor komende data."
            }
          },
          {
            "@type": "Question",
            "name": "Voor wie zijn familieopstellingen geschikt?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Familieopstellingen zijn geschikt voor iedereen die patronen wil doorbreken, inzicht wil krijgen in familie- of relatiepatronen, of worstelt met verlies, rouw of conflicten. Ook als je je plek in de familie of in relaties beter wilt begrijpen, of ruimte wilt maken voor groei en ontwikkeling, kan een familieopstelling helpen."
            }
          },
          {
            "@type": "Question",
            "name": "Wat kost een familieopstelling?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "De kosten voor een familieopstelling hangen af van je rol. Als vraagsteller (met je eigen vraag) betaal je €100 per opstellingenmiddag. Als representant (om te leren en ervaren) betaal je €75 per los ticket. Met een strippenkaart van €130 (3 strippen) betaal je als representant slechts €43,33 per deelname, wat een besparing van 42% betekent ten opzichte van losse tickets. De strippenkaart is geldig voor deelname als representant en heeft geen vervaldatum. Bekijk de agenda voor komende opstellingenmiddagen."
            }
          }
        ]
      })}
      </script>

      {/* Custom Luxury Header */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" aria-hidden="true" /> Terug naar home
            </Link>
          </Button>
          
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
            Familie<span className="italic font-light font-serif text-[#F9EBEF]">opstellingen</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
            Systemisch werk voor persoonlijke ontwikkeling en inzicht in familiepatronen
          </p>
        </div>
      </section>

      <section className="py-24 px-6 relative bg-[#FAEBEE]">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-6xl relative z-10">
          
          {/* Intro Section */}
          <div className="text-center max-w-3xl mx-auto mb-24">
            <p className="text-lg md:text-xl text-[#6b4912] leading-relaxed font-light">
              Voel je dat oude patronen zich steeds herhalen? Loop je vast in relaties, gezin of werk? Met een familieopstelling krijg je op een zachte maar heldere manier zicht op de dynamieken die jouw keuzes, relaties en gevoel beïnvloeden.
            </p>
          </div>

          {/* Two Tracks Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-24">
            
            {/* Wanneer je voelt dat iets niet klopt */}
            <SpotlightCard className="p-10">
              <div className="p-3 rounded-2xl bg-[#FAEBEE] text-[#854C73] mb-6 w-fit">
                <RotateCcw className="w-8 h-8" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-6 font-bold">
                Wanneer je voelt dat iets niet klopt
              </h3>
              <ul className="space-y-4 text-gray-600 leading-relaxed flex-grow">
                <li className="flex items-start gap-3">
                  <span className="text-[#854C73] text-lg mt-1">•</span>
                  <span>Er is spanning of conflict in de familie of in een relatie</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#854C73] text-lg mt-1">•</span>
                  <span>Je voelt je onzeker, ondergewaardeerd of gevangen in een rol</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#854C73] text-lg mt-1">•</span>
                  <span>Patronen van uitstel, ziekte, verdriet of conflict herhalen zich</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#854C73] text-lg mt-1">•</span>
                  <span>Je worstelt met verlies of rouw en weet niet goed hoe verder</span>
                </li>
              </ul>
            </SpotlightCard>

            {/* Wanneer je iets wilt ontwikkelen */}
            <SpotlightCard className="p-10">
              <div className="p-3 rounded-2xl bg-[#FAEBEE] text-[#854C73] mb-6 w-fit">
                <Search className="w-8 h-8" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-6 font-bold">
                Wanneer je iets wilt ontwikkelen
              </h3>
              <ul className="space-y-4 text-gray-600 leading-relaxed flex-grow">
                <li className="flex items-start gap-3">
                  <span className="text-[#854C73] text-lg mt-1">•</span>
                  <span>Je wilt je plek in de familie of in relaties beter begrijpen</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#854C73] text-lg mt-1">•</span>
                  <span>Je wilt inzicht in belangrijke keuzes voor je persoonlijke of professionele leven</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#854C73] text-lg mt-1">•</span>
                  <span>Je wilt harmonie en verbinding creëren in relaties</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#854C73] text-lg mt-1">•</span>
                  <span>Je wilt oude patronen doorbreken en ruimte maken voor groei</span>
                </li>
              </ul>
            </SpotlightCard>

          </div>

          {/* Closing Section - Maatwerk of Opstellingsdagen */}
          <div className="bg-[#854C73] rounded-[2.5rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 pointer-events-none">
              <GrainTexture opacity={0.2} />
              <GeoPattern />
              <div className="absolute top-0 left-0 w-[250px] h-[250px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] border border-[#FAEBEE]/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-[250px] h-[250px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] border border-[#FAEBEE]/10 rounded-full translate-x-1/2 translate-y-1/2"></div>
            </div>
            
            <div className="relative z-10 max-w-3xl mx-auto">
              <Target className="w-16 h-16 text-[#FAEBEE] mx-auto mb-8 opacity-90" strokeWidth={1} aria-hidden="true" />
              <h3 className="font-serif text-3xl md:text-5xl text-[#FAEBEE] mb-6">
                Maatwerk of <span className="italic font-light font-serif">Opstellingsdagen</span>
              </h3>
              <p className="text-[#FAEBEE]/80 text-lg md:text-xl leading-relaxed mb-10 font-light">
                Een familieopstelling kan onderdeel zijn van een langer coachtraject. Samen kijken we wat het beste past. De opstellingen vinden plaats in een veilige en respectvolle setting. <strong className="text-[#FAEBEE]">Wil je dit zelf ervaren?</strong> Bekijk de volgende data in mijn agenda.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button variant="white" size="lg" asChild className="px-10 py-6 text-lg shadow-xl hover:scale-105 transition-transform cursor-pointer">
                  <Link to="/1-op-1">1-op-1</Link>
                </Button>
                <Button variant="white" size="lg" asChild className="px-10 py-6 text-lg shadow-xl hover:scale-105 transition-transform cursor-pointer">
                  <Link to="/agenda">Naar opstellingsdagen</Link>
                </Button>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default FamilieOpstelling;
